if (!customElements.get('hh-collection-tabs')) {
  customElements.define('hh-collection-tabs', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.track = this.querySelector('[role="tabpanel"]');
      this.previous = this.querySelector('.hh-collection-tabs__arrow--previous');
      this.next = this.querySelector('.hh-collection-tabs__arrow--next');
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index), options);
        tab.addEventListener('keydown', event => {
          let target;
          if (event.key === 'ArrowRight') target = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') target = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') target = 0;
          if (event.key === 'End') target = this.tabs.length - 1;
          if (target === undefined) return;
          event.preventDefault();
          this.select(target);
          this.tabs[target].focus();
        }, options);
      });
      this.previous.addEventListener('click', () => this.move(-1), options);
      this.next.addEventListener('click', () => this.move(1), options);
      this.track.addEventListener('scroll', () => this.updateArrows(), { ...options, passive: true });
      this.resizeObserver = new ResizeObserver(() => this.updateArrows());
      this.resizeObserver.observe(this.track);
      this.prepareMotion();
      this.revealObserver = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.revealed = true;
          this.track.querySelectorAll('.hh-collection-tabs__item').forEach(card => card.classList.add('hh-motion-reveal'));
          this.revealObserver.disconnect();
        }
      }, {threshold: .1});
      this.revealObserver.observe(this);
      this.updateArrows();
    }
    prepareMotion() {
      this.track.querySelectorAll('.hh-collection-tabs__item').forEach((card, index) => {
        card.style.setProperty('--card', index);
        const gallery = card.querySelector('.card-gallery');
        // The outer track owns carousel navigation; avoid a second swipe region inside each product link.
        gallery?.querySelector('slideshow-component')?.setAttribute('disabled', 'true');
        const images = gallery?.querySelectorAll('slideshow-slide img');
        if (gallery && images?.length > 1 && !gallery.querySelector('.hh-motion-photo')) {
          const media = gallery.querySelector('slideshow-component') || gallery;
          media.classList.add('hh-motion-gallery');
          [images[0], images[1]].forEach((image, i) => {
            const clone = image.cloneNode();
            clone.removeAttribute('id'); clone.removeAttribute('ref');
            clone.alt = ''; clone.setAttribute('aria-hidden', 'true');
            clone.className = 'hh-motion-photo' + (i ? ' hh-motion-photo--second' : '');
            media.append(clone);
          });
        }
        if (this.revealed) card.classList.add('hh-motion-reveal');
      });
    }
    disconnectedCallback() { this.controller?.abort(); this.resizeObserver?.disconnect(); this.revealObserver?.disconnect(); }
    select(index) {
      const selected = this.tabs[index];
      if (selected.getAttribute('aria-selected') === 'true') return;
      this.tabs.forEach(tab => {
        tab.setAttribute('aria-selected', String(tab === selected));
        tab.tabIndex = tab === selected ? 0 : -1;
      });
      this.track.replaceChildren(this.querySelector(`template[data-collection-panel="${index}"]`).content.cloneNode(true));
      this.track.setAttribute('aria-labelledby', selected.id);
      this.track.scrollLeft = 0;
      this.prepareMotion();
      this.updateArrows();
    }
    move(direction) {
      const control = direction < 0 ? this.previous : this.next;
      if (control.getAttribute('aria-disabled') === 'true') return;
      const card = this.track.querySelector('.hh-collection-tabs__item');
      const distance = (card?.getBoundingClientRect().width ?? this.track.clientWidth) + parseFloat(getComputedStyle(this.track).gap || 0);
      this.track.scrollBy({ left: distance * direction, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }
    updateArrows() {
      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const controlsHaveFocus = this.previous === document.activeElement || this.next === document.activeElement;
      // Keep endpoint controls in place so activating the last step cannot discard focus.
      // Passive scrolling never moves focus, including when a resize removes overflow.
      const hideControls = maximum <= 2 && !controlsHaveFocus;
      this.previous.hidden = hideControls;
      this.next.hidden = hideControls;
      this.previous.setAttribute('aria-disabled', String(this.track.scrollLeft <= 2));
      this.next.setAttribute('aria-disabled', String(this.track.scrollLeft >= maximum - 2));
    }
  });
}
